import React, { useState } from 'react';
import {
  BarChart3,
  BarChart2,
  TrendingUp,
  Clock,
  Target,
  Activity,
  Timer,
  PieChart,
  ArrowUp,
  ArrowDown,
  Zap,
  TrendingDown,
  Building2,
  Users,
  Copy,
  CheckCircle,
  FileText
} from 'lucide-react';
import { KPICard } from './KPICard';
import { KPIMetrics, FilterState } from '../types';
import { formatVolume, formatPercentage, formatMarketCap } from '../utils/calculations';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

interface KPIDashboardProps {
  metrics: KPIMetrics;
  filters?: FilterState;
}

export const KPIDashboard: React.FC<KPIDashboardProps> = ({ metrics, filters }) => {
  const [copySuccess, setCopySuccess] = useState(false);

  const generateFiltersSummary = (): string => {
    if (!filters) return '';

    const parts: string[] = [];

    // Data Range
    if (filters.dateRange[0] && filters.dateRange[1]) {
      parts.push(`Periodo: ${format(filters.dateRange[0], 'dd/MM/yyyy', { locale: it })} - ${format(filters.dateRange[1], 'dd/MM/yyyy', { locale: it })}`);
    }

    // Open Price Range
    if (filters.openPriceRange[0] > 0 || filters.openPriceRange[1] < Infinity) {
      const minPrice = filters.openPriceRange[0] > 0 ? `$${filters.openPriceRange[0]}` : '$0';
      const maxPrice = filters.openPriceRange[1] < Infinity ? `$${filters.openPriceRange[1]}` : '∞';
      parts.push(`Prezzo Apertura: ${minPrice} - ${maxPrice}`);
    }

    // Gap Percent Range
    if (filters.gapPercentRange[0] > -Infinity || filters.gapPercentRange[1] < Infinity) {
      const minGap = filters.gapPercentRange[0] > -Infinity ? `${filters.gapPercentRange[0]}%` : '-∞';
      const maxGap = filters.gapPercentRange[1] < Infinity ? `${filters.gapPercentRange[1]}%` : '∞';
      parts.push(`Gap %: ${minGap} - ${maxGap}`);
    }

    // PM Volume Range
    if (filters.pmVolumeRange[0] > 0 || filters.pmVolumeRange[1] < Infinity) {
      const minVol = filters.pmVolumeRange[0] > 0 ? formatVolume(filters.pmVolumeRange[0]) : '0';
      const maxVol = filters.pmVolumeRange[1] < Infinity ? formatVolume(filters.pmVolumeRange[1]) : '∞';
      parts.push(`Volume PM: ${minVol} - ${maxVol}`);
    }

    // Market Cap Range
    if (filters.marketCapRange[0] > 0 || filters.marketCapRange[1] < Infinity) {
      const minCap = filters.marketCapRange[0] > 0 ? formatMarketCap(filters.marketCapRange[0]) : '$0';
      const maxCap = filters.marketCapRange[1] < Infinity ? formatMarketCap(filters.marketCapRange[1]) : '∞';
      parts.push(`Market Cap: ${minCap} - ${maxCap}`);
    }

    // Close Status
    if (filters.closeStatus !== 'all') {
      parts.push(`Chiusure: ${filters.closeStatus === 'red' ? 'Solo Rosse' : 'Solo Verdi'}`);
    }

    // PMH Break
    if (filters.pmhBreak !== 'all') {
      parts.push(`Break PMH: ${filters.pmhBreak === 'Y' ? 'Solo Sì' : 'Solo No'}`);
    }

    // Search Ticker
    if (filters.searchTicker) {
      parts.push(`Ticker: ${filters.searchTicker}`);
    }

    // HOD Time Filter
    if (filters.hodTimeFilter !== 'all') {
      parts.push(`HOD Time: ${filters.hodTimeFilter === 'before_1030' ? 'Prima 10:30' : 'Dopo 10:30'}`);
    }

    return parts.length > 0 ? parts.join(' | ') : 'Nessun filtro applicato';
  };

  const generateSummaryText = (): string => {
    const filtersSummary = generateFiltersSummary();

    return `RIEPILOGO METRICHE KPI
========================================

${filtersSummary ? `FILTRI APPLICATI\n----------------------------------------\n${filtersSummary}\n\n` : ''}METRICHE BASE
----------------------------------------
• Numero di Gap: ${metrics.numberOfGaps}
• Market Cap Media: ${formatMarketCap(metrics.averageMarketCap)} (Mediana: ${formatMarketCap(metrics.medianMarketCap)})
• Azioni in Circolazione Media: ${formatVolume(metrics.averageSharesOutstanding)} (Mediana: ${formatVolume(metrics.medianSharesOutstanding)})

METRICHE VOLUME
----------------------------------------
• Volume PM Medio: ${formatVolume(metrics.averagePmVolume)} (Mediana: ${formatVolume(metrics.medianPmVolume)})
• Volume EOD Medio: ${formatVolume(metrics.averageEodVolume)} (Mediana: ${formatVolume(metrics.medianEodVolume)})

METRICHE TEMPO E PERFORMANCE
----------------------------------------
• Orario HOD Medio: ${metrics.hodAverageTime}
• Orario LOD Medio: ${metrics.lodAverageTime}
• Spike % Medio: ${formatPercentage(metrics.averageSpikePercent)} (Mediana: ${formatPercentage(metrics.medianSpikePercent)})
• Vol 30 Min/Vol PM: ${metrics.average30MinVolPmVolRatio.toFixed(2)}x (Mediana: ${metrics.median30MinVolPmVolRatio.toFixed(2)}x)

METRICHE PERFORMANCE INTRADAY
----------------------------------------
• 30 Min High % Medio: ${formatPercentage(metrics.average30MinHighPercent)} (Mediana: ${formatPercentage(metrics.median30MinHighPercent)})
• 30 Min Low % Medio: ${formatPercentage(metrics.average30MinLowPercent)}
• Apertura a PMH Medio: ${formatPercentage(metrics.averageOpenToPmh)} (Mediana: ${formatPercentage(metrics.medianOpenToPmh)})
• PMH a High Medio: ${formatPercentage(metrics.averagePmhToHigh)} (Mediana: ${formatPercentage(metrics.medianPmhToHigh)})
• Chiusura Rossa %: ${metrics.closeRedPercentage.toFixed(1)}%
• Break PMH %: ${metrics.pmhBreakPercentage.toFixed(1)}%

METRICHE CHIUSURE
----------------------------------------
• Ritorno Medio Chiusure Positive: ${formatPercentage(metrics.averagePositiveChangePercent)}
• Ritorno Medio Chiusure Negative: ${formatPercentage(metrics.averageNegativeChangePercent)}`;
  };

  const handleCopySummary = async () => {
    try {
      await navigator.clipboard.writeText(generateSummaryText());
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Errore nella copia del riepilogo:', error);
    }
  };

  return (
    <div className="space-y-6 sm:space-y-8 mb-6 sm:mb-8">
      {/* Riga 1 - Metriche Base con Market Cap e Shares Outstanding */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <KPICard
          title="Numero di Gap"
          value={metrics.numberOfGaps}
          icon={BarChart3}
          description="Totale gap analizzati"
          color="blue"
        />
        
        <KPICard
          title="Market Cap Media"
          value={formatMarketCap(metrics.averageMarketCap)}
          icon={Building2}
          description="Capitalizzazione di mercato media"
          color="green"
          subtitle={`Mediana: ${formatMarketCap(metrics.medianMarketCap)}`}
        />
        
        <KPICard
          title="Azioni in Circolazione Media"
          value={formatVolume(metrics.averageSharesOutstanding)}
          icon={Users}
          description="Azioni in circolazione medie"
          color="yellow"
          subtitle={`Mediana: ${formatVolume(metrics.medianSharesOutstanding)}`}
        />
        
        <KPICard
          title="Ratio Volume PM/EOD"
          value={`${metrics.pmEodVolumeRatio.toFixed(1)}x`}
          icon={BarChart2}
          description="Volume fine giornata vs pre-market"
          color="blue"
          trend="up"
          subtitle={`Mediana: ${metrics.medianPmEodVolumeRatio.toFixed(1)}x`}
        />
      </div>

      {/* Riga 2 - Metriche Volume */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <KPICard
          title="Volume PM Medio"
          value={formatVolume(metrics.averagePmVolume)}
          icon={Activity}
          description="Volume trading pre-market"
          color="blue"
          subtitle={`Mediana: ${formatVolume(metrics.medianPmVolume)}`}
        />
        
        <KPICard
          title="Volume EOD Medio"
          value={formatVolume(metrics.averageEodVolume)}
          icon={TrendingUp}
          description="Volume trading fine giornata"
          color="green"
          subtitle={`Mediana: ${formatVolume(metrics.medianEodVolume)}`}
        />

        <KPICard
          title="Chiusura Rossa %"
          value={`${metrics.closeRedPercentage.toFixed(1)}%`}
          icon={Target}
          description="Percentuale di chiusure rosse"
          color="red"
          trend="down"
          progressValue={metrics.closeRedPercentage} // Barra accurata basata sul valore effettivo
        />
        
        <KPICard
          title="Break PMH %"
          value={`${metrics.pmhBreakPercentage.toFixed(1)}%`}
          icon={PieChart}
          description="Breakout massimo pre-market"
          color="blue"
          trend="up"
          progressValue={metrics.pmhBreakPercentage} // Barra accurata basata sul valore effettivo
        />
      </div>

      {/* Riga 3 - Metriche Tempo e Performance */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <KPICard
          title="Orario HOD Medio"
          value={metrics.hodAverageTime}
          icon={Clock}
          description="Timing medio massimo giornaliero"
          color="yellow"
          // Nessun progressValue - la barra non viene mostrata per gli orari
        />
        
        <KPICard
          title="Orario LOD Medio"
          value={metrics.lodAverageTime}
          icon={Timer}
          description="Timing medio minimo giornaliero"
          color="red"
          // Nessun progressValue - la barra non viene mostrata per gli orari
        />

        <KPICard
          title="Spike % Medio"
          value={formatPercentage(metrics.averageSpikePercent)}
          icon={Zap}
          description="Spike medio da apertura a massimo"
          color="yellow"
          trend={metrics.averageSpikePercent > 0 ? "up" : "down"}
          subtitle={`Mediana: ${formatPercentage(metrics.medianSpikePercent)}`}
          progressValue={Math.min(metrics.averageSpikePercent * 100 * 2, 100)} // Scala per visualizzazione
        />
        
        <KPICard
          title="Vol 30 Min/Vol PM"
          value={`${metrics.average30MinVolPmVolRatio.toFixed(2)}x`}
          icon={BarChart2}
          description="Ratio volume 30 minuti vs pre-market"
          color="blue"
          trend="neutral"
          subtitle={`Mediana: ${metrics.median30MinVolPmVolRatio.toFixed(2)}x`}
          progressValue={Math.min(metrics.average30MinVolPmVolRatio * 20, 100)} // Scala per visualizzazione
        />
      </div>

      {/* Riga 4 - Metriche Performance Intraday */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <KPICard
          title="30 Min High % Medio"
          value={formatPercentage(metrics.average30MinHighPercent)}
          icon={ArrowUp}
          description="Performance media massimo 30 minuti"
          color="green"
          trend={metrics.average30MinHighPercent > 0 ? "up" : "down"}
          subtitle={`Mediana: ${formatPercentage(metrics.median30MinHighPercent)}`}
          progressValue={Math.min(metrics.average30MinHighPercent * 100 * 2, 100)} // Scala per visualizzazione
        />
        
        <KPICard
          title="30 Min Low % Medio"
          value={formatPercentage(metrics.average30MinLowPercent)}
          icon={ArrowDown}
          description="Performance media minimo 30 minuti"
          color="red"
          trend={metrics.average30MinLowPercent > 0 ? "up" : "down"}
          progressValue={Math.min(Math.abs(metrics.average30MinLowPercent * 100 * 2), 100)} // Scala per visualizzazione
        />

        <KPICard
          title="Apertura a PMH Medio"
          value={formatPercentage(metrics.averageOpenToPmh)}
          icon={TrendingUp}
          description="Gap medio da apertura a massimo pre-market"
          color="blue"
          trend={metrics.averageOpenToPmh > 0 ? "up" : "down"}
          subtitle={`Mediana: ${formatPercentage(metrics.medianOpenToPmh)}`}
          progressValue={Math.min(Math.abs(metrics.averageOpenToPmh * 100 * 2), 100)} // Scala per visualizzazione
        />
        
        <KPICard
          title="PMH a High Medio"
          value={formatPercentage(metrics.averagePmhToHigh)}
          icon={Target}
          description="Estensione media da PMH a massimo giornaliero"
          color="green"
          trend={metrics.averagePmhToHigh > 0 ? "up" : "down"}
          subtitle={`Mediana: ${formatPercentage(metrics.medianPmhToHigh)}`}
          progressValue={Math.min(metrics.averagePmhToHigh * 100 * 2, 100)} // Scala per visualizzazione
        />
      </div>

      {/* Riga 5 - Nuove Metriche Change % per Chiusure Positive e Negative */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <KPICard
          title="Ritorno Medio Chiusure Positive"
          value={formatPercentage(metrics.averagePositiveChangePercent)}
          icon={TrendingUp}
          description="Variazione media quando l'azione chiude verde"
          color="green"
          trend="up"
          progressValue={Math.min(metrics.averagePositiveChangePercent * 100 * 2, 100)} // Scala per visualizzazione
        />

        <KPICard
          title="Ritorno Medio Chiusure Negative"
          value={formatPercentage(metrics.averageNegativeChangePercent)}
          icon={TrendingDown}
          description="Variazione media quando l'azione chiude rossa"
          color="red"
          trend="down"
          progressValue={Math.min(Math.abs(metrics.averageNegativeChangePercent * 100 * 2), 100)} // Scala per visualizzazione
        />
      </div>

      {/* Box Riepilogo Completo */}
      <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className="p-1.5 sm:p-2 bg-blue-400/10 rounded-lg">
              <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-blue-400" />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-white">Riepilogo Completo</h3>
              <p className="text-xs sm:text-sm text-gray-400">Tutti i valori KPI in formato copiabile</p>
            </div>
          </div>
          <button
            onClick={handleCopySummary}
            className={`flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 rounded-lg transition-all duration-200 text-sm ${
              copySuccess
                ? 'bg-green-600 text-white'
                : 'bg-blue-600 hover:bg-blue-500 text-white'
            }`}
          >
            {copySuccess ? (
              <>
                <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Copiato!</span>
              </>
            ) : (
              <>
                <Copy className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Copia Tutto</span>
              </>
            )}
          </button>
        </div>

        <div className="bg-gray-900/50 border border-gray-600 rounded-lg p-3 sm:p-4">
          <pre className="text-xs sm:text-sm text-gray-300 whitespace-pre-wrap font-mono leading-relaxed overflow-x-auto">
            {generateSummaryText()}
          </pre>
        </div>

        <div className="mt-3 text-xs text-gray-500">
          💡 Clicca "Copia Tutto" per copiare tutti i valori formattati negli appunti
        </div>
      </div>
    </div>
  );
};